import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Bars3Icon, XCircleIcon } from "@heroicons/react/24/outline";
import { adminService } from "../../../services/admin";
import { videoService } from "../../../services/videoService";
import { Folder, Video } from "./types";
import { getImageUrl } from "../../../services/api";

// Components
import VideoBreadcrumbs from "./components/VideoBreadcrumbs";
import VideoFilters from "./components/VideoFilters";
import VideoCard from "./components/VideoCard";
import FolderCard from "./components/FolderCard";
import FolderSidebar from "./components/FolderSidebar";
import VideoUploadModal from "./components/VideoUploadModal";
import FolderActionModal from "./components/FolderActionModal";
import MoveVideoModal from "./components/MoveVideoModal";
import MoveFolderModal from "./components/MoveFolderModal";
import VideoPlaybackModal from "./components/VideoPlaybackModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import CompactVideoHeader from "./components/CompactVideoHeader";
import SuccessMessage from "./components/SuccessMessage";

const Videos: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const [fileTypeFilter, setFileTypeFilter] = useState(
    searchParams.get("fileType") || "",
  );
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [eta, setETA] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Form state
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDescription, setVideoDescription] = useState("");

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [thumbnailColor, setThumbnailColor] = useState("#3B82F6"); // Default blue color
  const [videoSortOrder, setVideoSortOrder] = useState<number>(0);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [videoStreamUrl, setVideoStreamUrl] = useState<string>("");
  const [isLoadingStream, setIsLoadingStream] = useState(false);

  // Folder management state
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);
  const [folderPath, setFolderPath] = useState<Folder[]>([]);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [folderName, setFolderName] = useState("");
  const [folderDescription, setFolderDescription] = useState("");
  const [folderSortOrder, setFolderSortOrder] = useState<number>(0);
  const [selectedVideos, setSelectedVideos] = useState<number[]>([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState<number | null>(null);
  const [folderSearchTerm, setFolderSearchTerm] = useState("");
  const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
  const [movingFolder, setMovingFolder] = useState<Folder | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<number | null>(
    null,
  );

  // Helpers for folder tree flattening and descendant detection
  const flattenFolders = (
    nodes: Folder[],
    depth = 0,
  ): Array<{ id: number; name: string; depth: number }> => {
    const result: Array<{ id: number; name: string; depth: number }> = [];
    for (const node of nodes) {
      result.push({ id: node.id, name: node.name, depth });
      if (Array.isArray(node.children) && node.children.length > 0) {
        result.push(...flattenFolders(node.children, depth + 1));
      }
    }
    return result;
  };

  const findNodeById = (nodes: Folder[], id: number): Folder | undefined => {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (Array.isArray(n.children) && n.children.length > 0) {
        const found = findNodeById(n.children, id);
        if (found) return found;
      }
    }
    return undefined;
  };

  const findPathToNode = (
    nodes: Folder[],
    id: number,
    path: Folder[] = [],
  ): Folder[] | null => {
    for (const n of nodes) {
      if (n.id === id) return [...path, n];
      if (Array.isArray(n.children) && n.children.length > 0) {
        // @ts-ignore
        const foundPath = findPathToNode(n.children, id, [...path, n]);
        if (foundPath) return foundPath;
      }
    }
    return null;
  };

  const syncFolderFromUrl = async () => {
    const folderIdStr = searchParams.get("folderId");
    if (!folderIdStr) {
      if (currentFolder) {
        setCurrentFolder(null);
        setFolderPath([]);
      }
      return;
    }

    const folderId = parseInt(folderIdStr);
    if (isNaN(folderId)) return;

    try {
      // We need folders tree to build path
      if (folders.length === 0) return;

      const path = findPathToNode(folders, folderId);
      if (path) {
        const folder = path[path.length - 1];
        // Fetch full folder details (with videos)
        const response = await adminService.getFolder(folder.id);
        setCurrentFolder(response.folder);
        setFolderPath(path);
      }
    } catch (error) {
      console.error("Error syncing folder from URL:", error);
    }
  };

  useEffect(() => {
    syncFolderFromUrl();
  }, [searchParams, folders]);

  const collectDescendants = (node: Folder | undefined, set: Set<number>) => {
    if (!node || !Array.isArray(node.children) || node.children.length === 0)
      return;
    for (const child of node.children) {
      set.add(child.id);
      collectDescendants(child, set);
    }
  };

  const flattenedFolders = useMemo(() => flattenFolders(folders), [folders]);

  const excludedMoveFolderIds = useMemo(() => {
    if (!movingFolder) return new Set<number>();
    const set = new Set<number>();
    const node = findNodeById(folders, movingFolder.id);
    collectDescendants(node, set);
    set.add(movingFolder.id);
    return set;
  }, [movingFolder, folders]);
  // Global totals
  const [totalFolderCount, setTotalFolderCount] = useState<number>(0);
  const [totalVideoCount, setTotalVideoCount] = useState<number>(0);
  const [totalFileCount, setTotalFileCount] = useState<number>(0);

  // Responsive state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Real-time progress listener
  useEffect(() => {
    const handleUploadProgress = (event: CustomEvent) => {
      const { percentage, loaded, total, speed } = event.detail;
      setUploadProgress(percentage);

      if (speed) {
        setUploadSpeed(speed);
        const remaining = total - loaded;
        const timeRemaining = remaining / speed; // seconds
        setETA(timeRemaining);
      }

      console.log(
        `Real-time progress: ${percentage}% (${loaded}/${total} bytes, ${speed ? (speed / 1024 / 1024).toFixed(1) + " MB/s" : "calculating speed..."})`,
      );
    };

    window.addEventListener(
      "uploadProgress",
      handleUploadProgress as EventListener,
    );

    return () => {
      window.removeEventListener(
        "uploadProgress",
        handleUploadProgress as EventListener,
      );
    };
  }, []);

  const updateSearchParams = (params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);

    // Update params
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    fetchVideos();
  };

  const handleFileTypeFilterChange = (fileType: string) => {
    setFileTypeFilter(fileType);
    updateSearchParams({ fileType, page: "1" });
  };

  const openUploadModal = () => {
    // Calculate next number based on existing items (folders + videos + other files)
    const nextNumber =
      (currentFolder
        ? (currentFolder.video_count || 0) +
          (currentFolder.file_count || 0) +
          (currentFolder.subfolder_count || 0)
        : folders.length + totalVideoCount + totalFileCount) + 1;

    // Reset form state
    setVideoTitle("");
    setVideoDescription("");
    setVideoSortOrder(nextNumber);
    setVideoFile(null);
    setThumbnailFile(null);
    setSelectedFolderId(currentFolder?.id || null);
    setThumbnailColor("#3B82F6");
    setEditingVideo(null);
    setShowUploadModal(true);
  };

  const openEditModal = (video: Video) => {
    setEditingVideo(video);
    setVideoTitle(video.title);
    setVideoDescription(video.description);
    setVideoSortOrder(video.sort_order || 0);

    setSelectedFolderId(video.folder_id || null);
    setThumbnailColor(video.thumbnail_color || "#3B82F6"); // Use existing color or default
    // Clear any previously selected files when opening edit
    setVideoFile(null);
    setThumbnailFile(null);
    setShowUploadModal(true);
  };

  const closeModal = () => {
    setShowUploadModal(false);
    // Reset upload progress and state
    setUploadProgress(0);
    setUploadSpeed(0);
    setETA(0);
    setIsUploading(false);
    setUploadStatus("idle");
    setRetryAttempt(0);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setVideoFile(e.target.files[0]);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log("Thumbnail file selected:", {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
      });
      setThumbnailFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsUploading(true);
      setUploadStatus("uploading");
      setUploadProgress(0);
      setRetryAttempt(0);

      if (editingVideo) {
        // Handle video editing
        console.log("Editing video:", editingVideo.id);

        // Check if there are any files to upload
        const hasFiles = videoFile || thumbnailFile;

        if (hasFiles) {
          // Use FormData for file uploads
          const formData = new FormData();
          formData.append("title", videoTitle);
          formData.append("description", videoDescription);
          formData.append("sort_order", videoSortOrder.toString());
          formData.append("thumbnail_color", thumbnailColor);

          // Add folder_id if selected
          if (selectedFolderId !== null) {
            formData.append("folder_id", selectedFolderId.toString());
          } else {
            formData.append("folder_id", ""); // Empty string to clear folder
          }

          // If a new video file is selected, add it to the form data
          if (videoFile) {
            formData.append("video", videoFile);
          }

          // If a new thumbnail is selected, add it to the form data
          if (thumbnailFile) {
            console.log("Adding thumbnail to FormData:", {
              name: thumbnailFile.name,
              type: thumbnailFile.type,
              size: thumbnailFile.size,
            });
            formData.append("thumbnail", thumbnailFile);
          }

          console.log(
            "🔄 Calling updateVideo with files for ID:",
            editingVideo.id,
          );
          const result = await adminService.updateVideo(
            editingVideo.id,
            formData,
          );
          console.log("✅ Update result:", result);
        } else {
          // Use JSON for metadata-only updates (much faster)
          const updateData = {
            title: videoTitle,
            description: videoDescription,
            sort_order: videoSortOrder,
            thumbnail_color: thumbnailColor,
            folder_id: selectedFolderId !== null ? selectedFolderId : null,
          };

          console.log(
            "🔄 Calling updateVideo with metadata only for ID:",
            editingVideo.id,
          );
          const result = await adminService.updateVideoMetadata(
            editingVideo.id,
            updateData,
          );
          console.log("✅ Update result:", result);
        }

        // Success
        setUploadStatus("success");
        setTimeout(() => {
          closeModal();
          fetchVideos();
          fetchFolders(); // Also refresh folders to update video counts
        }, 1000);
        return;
      }

      // Handle new video upload
      if (!videoFile) {
        alert("Please select a video file to upload");
        setIsUploading(false);
        setUploadStatus("idle");
        return;
      }

      console.log("Starting unlimited timeout video upload...");
      console.log(
        "Video file:",
        videoFile.name,
        videoFile.size,
        videoFile.type,
      );
      console.log("Form data:", {
        title: videoTitle,
        description: videoDescription,
      });

      // Create form data for new video
      const formData = new FormData();
      formData.append("title", videoTitle);
      formData.append("description", videoDescription || "");
      formData.append("sort_order", videoSortOrder.toString());

      formData.append("video", videoFile);
      // Add folder_id if selected
      if (selectedFolderId) {
        formData.append("folder_id", selectedFolderId.toString());
      }
      // Add thumbnail if selected
      if (thumbnailFile) {
        console.log("Adding thumbnail to new video FormData:", {
          name: thumbnailFile.name,
          type: thumbnailFile.type,
          size: thumbnailFile.size,
        });
        formData.append("thumbnail", thumbnailFile);
      }

      // Add thumbnail color
      formData.append("thumbnail_color", thumbnailColor);

      // Log FormData contents
      console.log("FormData contents:");
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(
            `${key}: File - ${value.name} (${value.size} bytes, ${value.type})`,
          );
        } else {
          console.log(`${key}: ${value}`);
        }
      }

      console.log("Calling adminService.uploadVideo with unlimited timeout...");

      // Upload video with retry mechanism
      const response = await adminService.uploadVideo(formData);

      console.log("Upload successful:", response);

      // Success
      setUploadProgress(100);
      setUploadStatus("success");

      // Refresh video list after a short delay
      setTimeout(() => {
        closeModal();
        fetchVideos();
        fetchFolders(); // Also refresh folders to update video counts
      }, 1000);
    } catch (error: any) {
      console.error("Error handling video:", error);
      setUploadStatus("error");

      // Log detailed error information
      if (error instanceof Error) {
        console.error("Error name:", error.name);
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }

      // Check for network/API specific errors
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        console.error("Response headers:", error.response.headers);

        // Show more specific error messages based on status
        const status = error.response.status;
        const data = error.response.data;

        let errorMessage = editingVideo
          ? "ການອັບເດດວິດີໂອລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່."
          : "ການອັບໂຫລດວິດີໂອລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.";

        if (status === 401) {
          errorMessage = "ການຢັ້ງຢືນລົ້ມເຫລວ. ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່.";
        } else if (status === 403) {
          errorMessage = "ທ່ານບໍ່ມີສິດໃນການດຳເນີນການນີ້.";
        } else if (status === 413) {
          errorMessage = "ໄຟລ໌ໃຫຍ່ເກີນໄປ. ກະລຸນາເລືອກໄຟລ໌ວິດີໂອທີ່ນ້ອຍກວ່າ.";
        } else if (status === 400 && data && data.message) {
          errorMessage = data.message;
        } else if (status === 500) {
          errorMessage = "ຂໍ້ຜິດພາດເຊີເວີ. ກະລຸນາລອງໃໝ່ໃນພາຍຫຼັງ.";
        } else if (status === 201) {
          // Success case - even though it's in error handler
          console.log("Upload actually succeeded (201), but caught as error");
          setUploadStatus("success");
          setTimeout(() => {
            closeModal();
            fetchVideos();
          }, 1000);
          return;
        }

        alert(errorMessage);
      } else if (error.request) {
        console.error(
          "Request was made but no response received:",
          error.request,
        );

        // With no timeout, this should be rare, but handle anyway
        if (
          error.code === "ECONNABORTED" ||
          error.message.includes("timeout")
        ) {
          alert(
            "ການເຊື່ອມຕໍ່ອັບໂຫລດຖືກຂັດຂວາງ. ກະລຸນາກວດສອບອິນເຕີເນັດຂອງທ່ານ ແລະ ລອງໃໝ່.",
          );
        } else {
          alert(
            "ຂໍ້ຜິດພາດເຄືອຂ່າຍ. ກະລຸນາກວດສອບການເຊື່ອມຕໍ່ຂອງທ່ານ ແລະ ລອງໃໝ່.",
          );
        }
      } else {
        console.error("Error setting up request:", error.message);
        alert(
          editingVideo
            ? "ການອັບເດດວິດີໂອລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່."
            : "ການອັບໂຫລດວິດີໂອລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.",
        );
      }

      setIsUploading(false);
    }
  };

  const [showVideoDeleteConfirm, setShowVideoDeleteConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<Video | null>(null);

  const handleDeleteVideo = (videoId: number) => {
    const video = videos.find((v) => v.id === videoId) || null;
    setVideoToDelete(video);
    setShowVideoDeleteConfirm(!!video);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;
    try {
      setActionLoading(videoToDelete.id);
      await adminService.deleteVideo(videoToDelete.id);
      setShowSuccessMessage(
        `Video "${videoToDelete.title}" deleted successfully!`,
      );
      setTimeout(() => setShowSuccessMessage(null), 3000);
      setShowVideoDeleteConfirm(false);
      setVideoToDelete(null);
      fetchVideos();
    } catch (error) {
      console.error("Error deleting video:", error);
      setShowSuccessMessage("Failed to delete video. Please try again.");
      setTimeout(() => setShowSuccessMessage(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePlayVideo = async (video: Video) => {
    const isVideo = (video.file_type || "video") === "video";

    if (!isVideo) {
      if (!video.cloudinary_url) return;

      // Use getImageUrl to handle both Cloudinary and local backend URLs
      let fileUrl = getImageUrl(video.cloudinary_url);

      // If it's a Cloudinary URL, force download by adding fl_attachment
      if (
        fileUrl.includes("res.cloudinary.com") &&
        fileUrl.includes("/upload/")
      ) {
        fileUrl = fileUrl.replace("/upload/", "/upload/fl_attachment/");
      }

      // Create a temporary link to trigger download
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute(
        "download",
        video.original_filename || video.title || "file",
      );
      link.target = "_blank"; // Fallback for some browsers
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    setCurrentVideo(video);
    setShowVideoModal(true);
    setIsLoadingStream(true);

    try {
      // Get the stream URL from the video service
      const { streamUrl } = await videoService.getVideoStreamUrl(video.id);
      setVideoStreamUrl(streamUrl);
    } catch (error) {
      console.error("Error getting video stream:", error);
    } finally {
      setIsLoadingStream(false);
    }
  };

  const closeVideoModal = () => {
    setShowVideoModal(false);
    setCurrentVideo(null);
    setVideoStreamUrl("");
  };

  // Folder management functions
  const fetchFolders = async () => {
    try {
      const response: any = await adminService.getFolders();
      setFolders(response.folders || []);
      // Prefer flat count if available
      if (Array.isArray(response.flat)) {
        setTotalFolderCount(response.flat.length);
      } else if (Array.isArray(response.folders)) {
        // Fallback: count all nodes in tree
        const countNodes = (nodes: Folder[]): number =>
          nodes.reduce(
            (acc, node) =>
              acc +
              1 +
              (Array.isArray(node.children) ? countNodes(node.children) : 0),
            0,
          );
        setTotalFolderCount(countNodes(response.folders));
      }
    } catch (error: any) {
      console.error("Error fetching folders:", error);
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
      }
    }
  };

  const navigateToFolder = (folder: Folder) => {
    setSearchParams((prev) => {
      prev.set("folderId", folder.id.toString());
      return prev;
    });
    setSelectedVideos([]);
  };

  const navigateToParent = () => {
    if (folderPath.length > 0) {
      const parent =
        folderPath.length > 1 ? folderPath[folderPath.length - 2] : null;
      setSearchParams((prev) => {
        if (parent) {
          prev.set("folderId", parent.id.toString());
        } else {
          prev.delete("folderId");
        }
        return prev;
      });
      setSelectedVideos([]);
    }
  };

  const navigateToRoot = () => {
    setSearchParams((prev) => {
      prev.delete("folderId");
      return prev;
    });
    setSelectedVideos([]);
  };

  const openCreateFolderModal = () => {
    // Calculate next number based on existing items (folders + videos + other files)
    const nextNumber =
      (currentFolder
        ? (currentFolder.video_count || 0) +
          (currentFolder.file_count || 0) +
          (currentFolder.subfolder_count || 0)
        : folders.length + totalVideoCount + totalFileCount) + 1;

    setEditingFolder(null);
    setFolderName("");
    setFolderDescription("");
    setFolderSortOrder(nextNumber);
    setShowFolderModal(true);
  };

  const openEditFolderModal = (folder: Folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderDescription(folder.description || "");
    setFolderSortOrder(folder.sort_order || 0);
    setShowFolderModal(true);
  };

  const closeFolderModal = () => {
    setShowFolderModal(false);
    setEditingFolder(null);
    setFolderName("");
    setFolderDescription("");
  };

  const handleFolderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!folderName.trim()) {
      alert("Please enter a folder name");
      return;
    }

    try {
      let response;
      if (editingFolder) {
        response = await adminService.updateFolder(editingFolder.id, {
          name: folderName,
          description: folderDescription,
          sort_order: folderSortOrder,
        });
      } else {
        response = await adminService.createFolder({
          name: folderName,
          description: folderDescription,
          parent_id: currentFolder?.id,
          sort_order: folderSortOrder,
        });
      }

      // Show success message
      const action = editingFolder ? "updated" : "created";
      setShowSuccessMessage(`Folder ${action} successfully!`);

      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccessMessage(null), 3000);

      closeFolderModal();
      fetchFolders();
      if (currentFolder) {
        navigateToFolder(currentFolder);
      }
    } catch (error: any) {
      console.error("Error saving folder:", error);

      // Log detailed error information
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        const errorMessage = error.response.data?.message || "Unknown error";
        setShowSuccessMessage(`ການບັນທຶກໂຟລເດີລົ້ມເຫລວ: ${errorMessage}`);
      } else if (error.request) {
        console.error(
          "Request was made but no response received:",
          error.request,
        );
        setShowSuccessMessage(
          "ຂໍ້ຜິດພາດເຄືອຂ່າຍ. ກະລຸນາກວດສອບການເຊື່ອມຕໍ່ຂອງທ່ານ ແລະ ລອງໃໝ່.",
        );
      } else {
        console.error("Error setting up request:", error.message);
        setShowSuccessMessage("ການບັນທຶກໂຟລເດີລົ້ມເຫລວ. ກະລຸນາລອງໃໝ່.");
      }

      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowSuccessMessage(null), 5000);
    }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState<string | null>(
    null,
  );

  const handleDeleteFolder = async (folder: Folder) => {
    setFolderToDelete(folder);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      await adminService.deleteFolder(folderToDelete.id);
      setShowSuccessMessage(
        `Folder "${folderToDelete.name}" deleted successfully!`,
      );
      setShowDeleteConfirm(false);
      setFolderToDelete(null);

      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccessMessage(null), 3000);

      fetchFolders();
      if (currentFolder?.id === folderToDelete.id) {
        navigateToParent();
      }
    } catch (error: any) {
      console.error("Error deleting folder:", error);

      if (error.response) {
        const errorMessage = error.response.data?.message || "Unknown error";
        setShowSuccessMessage(`Failed to delete folder: ${errorMessage}`);
      } else {
        setShowSuccessMessage("Failed to delete folder. Please try again.");
      }

      setShowDeleteConfirm(false);
      setFolderToDelete(null);

      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowSuccessMessage(null), 5000);
    }
  };

  const handleVideoSelection = (videoId: number) => {
    setSelectedVideos((prev) =>
      prev.includes(videoId)
        ? prev.filter((id) => id !== videoId)
        : [...prev, videoId],
    );
  };

  const selectAllVideos = () => {
    setSelectedVideos(videos.map((video) => video.id));
  };

  const clearVideoSelection = () => {
    setSelectedVideos([]);
  };

  const openMoveModal = () => {
    setShowMoveModal(true);
    setTargetFolderId(null);
  };

  const openMoveFolderModal = (folder: Folder) => {
    setMovingFolder(folder);
    setMoveTargetFolderId(null);
    setShowMoveFolderModal(true);
  };

  const closeMoveFolderModal = () => {
    setShowMoveFolderModal(false);
    setMovingFolder(null);
    setMoveTargetFolderId(null);
  };

  const handleMoveFolder = async () => {
    if (!movingFolder) {
      return;
    }

    try {
      // When moveTargetFolderId is null, we want to move to root (parent_id = null)
      // When moveTargetFolderId has a value, we want to move to that specific folder
      const parentId = moveTargetFolderId === null ? null : moveTargetFolderId;

      await adminService.updateFolder(movingFolder.id, {
        name: movingFolder.name,
        description: movingFolder.description,
        parent_id: parentId,
      });

      setShowSuccessMessage(
        `Folder "${movingFolder.name}" moved successfully!`,
      );

      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccessMessage(null), 3000);

      closeMoveFolderModal();
      fetchFolders();
      if (currentFolder) {
        navigateToFolder(currentFolder);
      }
    } catch (error: any) {
      console.error("Error moving folder:", error);

      if (error.response) {
        const errorMessage = error.response.data?.message || "Unknown error";
        setShowSuccessMessage(`Failed to move folder: ${errorMessage}`);
      } else {
        setShowSuccessMessage("Failed to move folder. Please try again.");
      }

      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowSuccessMessage(null), 5000);
    }
  };

  const closeMoveModal = () => {
    setShowMoveModal(false);
    setTargetFolderId(null);
  };

  const handleMoveVideos = async () => {
    if (selectedVideos.length === 0) {
      return;
    }

    try {
      if (targetFolderId === null) {
        // Move to root - update videos to have no folder_id
        for (const videoId of selectedVideos) {
          const formData = new FormData();
          formData.append("folder_id", "");
          await adminService.updateVideo(videoId, formData);
        }
      } else if (targetFolderId) {
        // Move to specific folder
        await adminService.moveVideosToFolder(targetFolderId, selectedVideos);
      }

      setShowSuccessMessage(
        `${selectedVideos.length} videos moved successfully!`,
      );

      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccessMessage(null), 3000);

      setSelectedVideos([]);
      closeMoveModal();
      fetchVideos();
      if (currentFolder) {
        navigateToFolder(currentFolder);
      }
    } catch (error: any) {
      console.error("Error moving videos:", error);

      if (error.response) {
        const errorMessage = error.response.data?.message || "Unknown error";
        setShowSuccessMessage(`Failed to move videos: ${errorMessage}`);
      } else {
        setShowSuccessMessage("Failed to move videos. Please try again.");
      }

      // Auto-hide error message after 5 seconds
      setTimeout(() => setShowSuccessMessage(null), 5000);
    }
  };

  const fetchVideos = async () => {
    try {
      setIsLoading(true);

      let response;

      if (currentFolder) {
        // Fetch videos from current folder
        const folderResponse = await adminService.getFolder(currentFolder.id);
        const folderVideos: Video[] = folderResponse.folder.videos || [];

        // Apply client-side filters when inside a folder
        const filteredInFolder = folderVideos.filter((v) => {
          const matchesFileType = fileTypeFilter
            ? v.file_type === fileTypeFilter
            : true;
          const matchesStatus = statusFilter
            ? v.status === (statusFilter as any)
            : true;
          const matchesSearch = searchTerm
            ? v.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              v.description?.toLowerCase().includes(searchTerm.toLowerCase())
            : true;
          return matchesFileType && matchesStatus && matchesSearch;
        });

        setVideos(filteredInFolder);
      } else {
        // Fetch all videos (original logic)
        const params = {
          page: 1,
          limit: 1000,
        };

        if (searchTerm) {
          // @ts-ignore - Add search parameter if provided
          params.search = searchTerm;
        }

        if (statusFilter) {
          // @ts-ignore - Add status filter if provided
          params.status = statusFilter;
        }

        response = await adminService.getVideos(
          1,
          1000,
          searchTerm,
          !searchTerm, // Only filter by root if not searching
        );

        // Normalize videos array from various possible response shapes
        const videosArray: Video[] = response?.videos
          ? response.videos
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];

        // Apply client-side filters
        const filtered = videosArray.filter((v) => {
          const matchesStatus = statusFilter
            ? v.status === (statusFilter as any)
            : true;
          const matchesFileType = fileTypeFilter
            ? (v.file_type || "video") === fileTypeFilter
            : true;
          return matchesStatus && matchesFileType;
        });

        setVideos(filtered);
      }
      // Fetch global total videos (unfiltered)
      try {
        const globalResp: any = await adminService.getVideos(1, 1);
        const globalTotalV =
          globalResp?.pagination?.videoCount ||
          globalResp?.pagination?.total ||
          0;
        const globalTotalF = globalResp?.pagination?.fileCount || 0;
        setTotalVideoCount(Number(globalTotalV) || 0);
        setTotalFileCount(Number(globalTotalF) || 0);
      } catch (e) {
        // ignore silently, keep previous value
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching videos:", err);
      setError("Failed to fetch videos");
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [searchTerm, statusFilter, fileTypeFilter, currentFolder]);

  useEffect(() => {
    fetchFolders();
  }, []);

  if (isLoading && videos.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <FolderSidebar
        sidebarOpen={sidebarOpen}
        isMobile={isMobile}
        setSidebarOpen={setSidebarOpen}
        folders={folders}
        currentFolder={currentFolder}
        folderSearchTerm={folderSearchTerm}
        setFolderSearchTerm={setFolderSearchTerm}
        navigateToFolder={navigateToFolder}
        navigateToRoot={navigateToRoot}
        openCreateFolderModal={openCreateFolderModal}
        openEditFolderModal={openEditFolderModal}
        openMoveFolderModal={openMoveFolderModal}
        handleDeleteFolder={handleDeleteFolder}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="px-3 sm:px-4 lg:px-6 xl:px-8 py-4 lg:py-6">
          {/* Mobile Header */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="ເປີດເມນູ"
              aria-label="ເປີດເມນູ"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-bold text-gray-900 flex items-center">
              {currentFolder ? currentFolder.name : "ວິດີໂອ"}
            </h1>
          </div>

          <VideoBreadcrumbs
            folderPath={folderPath}
            navigateToRoot={navigateToRoot}
            onBreadcrumbClick={(folder) => navigateToFolder(folder)}
          />

          <CompactVideoHeader
            currentFolder={currentFolder}
            totalFolderCount={totalFolderCount}
            totalVideoCount={totalVideoCount}
            totalFileCount={totalFileCount}
            openCreateFolderModal={openCreateFolderModal}
            openUploadModal={openUploadModal}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            handleSearch={handleSearch}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            fileTypeFilter={fileTypeFilter}
            setFileTypeFilter={handleFileTypeFilterChange}
            resetFilters={() => {
              setSearchTerm("");
              setStatusFilter("");
              setFileTypeFilter("");
            }}
            videoCount={videos.length}
            selectedCount={selectedVideos.length}
            onSelectAll={selectAllVideos}
            onClearSelection={clearVideoSelection}
            onMoveSelected={openMoveModal}
          />

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Folders Section */}
          {/* Folders Section - Display if current folder has subfolders OR if at root */}
          {((currentFolder &&
            currentFolder.subfolders &&
            currentFolder.subfolders.length > 0) ||
            (!currentFolder && folders.length > 0 && !searchTerm)) && (
            <div className="mt-8">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full mr-3"></div>
                ໂຟລເດີ
              </h2>
              <div className="grid grid-cols-1 gap-4 lg:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                {(currentFolder ? currentFolder.subfolders || [] : folders).map(
                  (folder, index) => (
                    <FolderCard
                      key={`folder-card-${folder.id}-${index}`}
                      folder={folder}
                      onNavigate={() => navigateToFolder(folder)}
                      onEdit={() => openEditFolderModal(folder)}
                      onMove={() => openMoveFolderModal(folder)}
                      onDelete={() => handleDeleteFolder(folder)}
                    />
                  ),
                )}
              </div>
            </div>
          )}

          {/* Videos Grid */}
          <div className="mt-8 grid grid-cols-2 gap-4 lg:gap-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {isLoading ? (
              <div className="col-span-full flex justify-center py-16">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
              </div>
            ) : videos.length === 0 ? (
              (
                currentFolder
                  ? (currentFolder.subfolders?.length || 0) > 0
                  : folders.length > 0
              ) ? null : (
                <div className="col-span-full text-center py-16 text-gray-500">
                  <span className="mx-auto h-16 w-16 text-gray-400 text-6xl block">
                    📹
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">
                    ບໍ່ພົບວິດີໂອ
                  </h3>
                  <p className="mt-2 text-base text-gray-500">
                    {searchTerm
                      ? "ບໍ່ພົບວິດີໂອທີ່ກົງກັບການຄົ້ນຫາຂອງທ່ານ."
                      : "ຍັງບໍ່ມີວິດີໂອໃນລະບົບ."}
                  </p>
                </div>
              )
            ) : (
              videos.map((video, index) => (
                <VideoCard
                  key={`video-${video.id}-${index}`}
                  video={video}
                  isSelected={selectedVideos.includes(video.id)}
                  onSelect={() => handleVideoSelection(video.id)}
                  onPlay={() => handlePlayVideo(video)}
                  onEdit={() => openEditModal(video)}
                  onDelete={() => handleDeleteVideo(video.id)}
                  isActionLoading={actionLoading === video.id}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <VideoUploadModal
        show={showUploadModal}
        onClose={closeModal}
        editingVideo={editingVideo}
        videoTitle={videoTitle}
        setVideoTitle={setVideoTitle}
        videoDescription={videoDescription}
        setVideoDescription={setVideoDescription}
        videoSortOrder={videoSortOrder}
        setVideoSortOrder={setVideoSortOrder}
        videoFile={videoFile}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        flattenedFolders={flattenedFolders}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        uploadStatus={uploadStatus}
        uploadSpeed={uploadSpeed}
        eta={eta}
        thumbnailColor={thumbnailColor}
        setThumbnailColor={setThumbnailColor}
        handleSubmit={handleSubmit}
        handleFileChange={handleFileChange}
        handleThumbnailChange={handleThumbnailChange}
        thumbnailFile={thumbnailFile}
      />

      <VideoPlaybackModal
        show={showVideoModal}
        video={currentVideo}
        onClose={closeVideoModal}
        isLoading={isLoadingStream}
        streamUrl={videoStreamUrl}
      />

      <FolderActionModal
        show={showFolderModal}
        onClose={closeFolderModal}
        editingFolder={editingFolder}
        folderName={folderName}
        setFolderName={setFolderName}
        folderDescription={folderDescription}
        setFolderDescription={setFolderDescription}
        folderSortOrder={folderSortOrder}
        setFolderSortOrder={setFolderSortOrder}
        handleSubmit={handleFolderSubmit}
      />

      <MoveVideoModal
        show={showMoveModal}
        onClose={closeMoveModal}
        selectedCount={selectedVideos.length}
        targetFolderId={targetFolderId}
        setTargetFolderId={setTargetFolderId}
        flattenedFolders={flattenedFolders}
        onMove={handleMoveVideos}
      />

      <MoveFolderModal
        show={showMoveFolderModal}
        onClose={closeMoveFolderModal}
        movingFolder={movingFolder}
        moveTargetFolderId={moveTargetFolderId}
        setMoveTargetFolderId={setMoveTargetFolderId}
        flattenedFolders={flattenedFolders}
        excludedMoveFolderIds={excludedMoveFolderIds}
        onMove={handleMoveFolder}
      />

      <DeleteConfirmModal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteFolder}
        title="ລຶບໂຟລເດີ"
        message="ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບໂຟລເດີນີ້? ການກະທຳນີ້ຈະລຶບໂຟລເດີຍ່ອຍແລະວິດີໂອທັງໝົດທີ່ຢູ່ຂ້າງໃນ."
        itemTitle={folderToDelete?.name || ""}
      />

      <DeleteConfirmModal
        show={showVideoDeleteConfirm}
        onClose={() => setShowVideoDeleteConfirm(false)}
        onConfirm={confirmDeleteVideo}
        title="ລຶບວິດີໂອ"
        message="ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບວິດີໂອນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້."
        itemTitle={videoToDelete?.title || ""}
      />

      <SuccessMessage
        message={showSuccessMessage}
        onClose={() => setShowSuccessMessage(null)}
      />
    </div>
  );
};

export default Videos;
